import React, { useEffect, useId, useMemo } from 'react';

import {
  PlatformFilter,
  usePlatformUpdateFilterTeam,
  usePlatformTableSaveFilterTeam,
} from '@cloudquery/api-platform';
import { useBaseAuthenticated } from '@cloudquery/base-users';
import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';

import {
  ErrorMessage,
  yup,
  YupInferType,
  handleApiFormError,
  DetailsDrawer,
  CLOUD_ASSET_TABLE_NAME,
  trackEvent,
  getYupValidationResolver,
  CloseButton,
} from 'shared/base';

import { useTableStateContext } from '../../hooks/useTableStateContext';
import { displayFilterNodes } from '../../utils/tableState';

interface Props {
  filter?: PlatformFilter;
  onClose: () => void;
  onSaved: (title: string) => void;
  open: boolean;
}

export function FilterFormDialog({ filter: platformFilter, onClose, onSaved, open }: Props) {
  const { currentMembership } = useBaseAuthenticated();
  const { applySavedFilter, filter: currentDataFilter } = useTableStateContext();
  const { mutateAsync: updateFilter } = usePlatformUpdateFilterTeam();
  const { mutateAsync: saveFilter } = usePlatformTableSaveFilterTeam();
  const isEditMode = !!platformFilter;

  const dialogLabelId = useId();
  const {
    defaultValues,
    resolver,
    schema: _schema,
  } = useMemo(() => {
    const schema = yup.object({
      name: yup
        .string()
        .default('')
        .trim()
        .required()
        .default(platformFilter?.name ?? ''),
    });

    return {
      defaultValues: schema.getDefault(),
      resolver: getYupValidationResolver(schema),
      schema,
    };
  }, [platformFilter]);

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    reset,
    setError,
  } = useForm({
    defaultValues,
    resolver,
  });

  const triggerUpdate = async (name: string) => {
    if (!platformFilter) {
      return;
    }
    await updateFilter({
      data: {
        expression: platformFilter.expression,
        name,
      },
      filterId: platformFilter.id,
      teamName: currentMembership.team.name,
    });

    applySavedFilter(platformFilter.id, platformFilter.expression);

    return;
  };

  const triggerSave = async (name: string) => {
    const result = await saveFilter({
      data: {
        expression: currentDataFilter,
        name,
      },
      tableName: CLOUD_ASSET_TABLE_NAME,
      teamName: currentMembership.team.name,
    });
    applySavedFilter(result.data.id, currentDataFilter);

    return;
  };

  const onSubmit: SubmitHandler<YupInferType<typeof _schema>> = async (formData) => {
    const { name } = formData;

    try {
      await (isEditMode ? triggerUpdate(name) : triggerSave(name));
      reset(defaultValues);

      if (!isEditMode) {
        trackEvent('inventory_filter_saved');
      }

      onSaved(name);
    } catch (error) {
      handleApiFormError(error, formData, setError);
    }
  };

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  return (
    <DetailsDrawer
      anchor="right"
      aria-labelledby={dialogLabelId}
      onClose={onClose}
      open={open}
      slotProps={{
        paper: {
          sx: { maxWidth: '100%', width: '500px' },
        },
      }}
    >
      <Stack
        autoComplete="off"
        component="form"
        noValidate={true}
        onSubmit={handleSubmit(onSubmit)}
        spacing={3}
      >
        <Box alignItems="center" display="flex" justifyContent="space-between">
          <Typography id={dialogLabelId} variant="h6">
            {isEditMode ? `Edit ${platformFilter?.name} Filter` : 'Create new filter'}
          </Typography>
          <CloseButton onClose={onClose} />
        </Box>
        <Stack>
          <Stack justifyContent="flex-start" spacing={2}>
            <Stack gap={0.5}>
              <Typography variant="body1">Filter properties</Typography>
              <Typography variant="body2">
                {displayFilterNodes(
                  isEditMode ? platformFilter?.expression : currentDataFilter,
                ).map((node: string) => (
                  <div key={node}>{node}</div>
                ))}
              </Typography>
            </Stack>
            <Controller
              control={control}
              name="name"
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  autoFocus={true}
                  disabled={isSubmitting}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message ?? 'Identifying name of your filter'}
                  label="Filter name"
                  size="small"
                  style={{ flexGrow: 1 }}
                  variant="outlined"
                />
              )}
            />
          </Stack>
          <ErrorMessage message={errors?.root?.message} sx={{ textAlign: 'right' }} />
        </Stack>
        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <Button color="secondary" onClick={onClose} size="medium" variant="outlined">
            Cancel
          </Button>
          <Button
            endIcon={<AddIcon />}
            loading={isSubmitting}
            size="medium"
            type="submit"
            variant="contained"
          >
            {isEditMode ? 'Save filter' : 'Create filter'}
          </Button>
        </Stack>
      </Stack>
    </DetailsDrawer>
  );
}
